import { useEffect, useState } from "react";
import { useVault } from "../hooks/useVault.jsx";
import { useToast } from "../hooks/useToast.jsx";
import { passwordStrength } from "../lib/crypto.js";
import * as Biometric from "../lib/biometric.js";
import * as api from "../lib/api.js";
import { LockIcon, FingerprintIcon, SunIcon, MoonIcon } from "../components/Icons.jsx";
import { ConfirmDialog } from "../components/Modal.jsx";

/**
 * Setup / unlock / join. Which form shows is decided by whether this device
 * already holds a vault, plus whichever the user navigates to.
 */
export function LockScreen({ theme, onToggleTheme }) {
  const { hasVault, create, unlock, join } = useVault();
  const toast = useToast();

  const [mode, setMode] = useState(hasVault ? "unlock" : "setup");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const [bioReady, setBioReady] = useState(false);
  // null = not yet known; true = anyone may create a vault here.
  const [registrationOpen, setRegistrationOpen] = useState(null);

  useEffect(() => {
    Biometric.canUnlock?.() ? setBioReady(true) : setBioReady(false);
    api.health()
      .then((h) => setRegistrationOpen(h.registrationOpen !== false))
      .catch(() => setRegistrationOpen(true)); // offline: let them create locally
  }, []);

  const guard = async (fn) => {
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lock-screen">
      <div className="lock-wrap">
        <div className="lock-card">
          <div className="brand">
            <div className="brand-mark">A</div>
            <div style={{ flex: 1 }}>
              <div className="brand-title">AIDIA</div>
              <div className="brand-sub">AI Digital Diary &amp; Secure Vault</div>
            </div>
            <button
              className="icon-btn"
              onClick={onToggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              {theme === "dark" ? <SunIcon /> : <MoonIcon />}
            </button>
          </div>

          {mode === "setup" && (
            <SetupForm
              busy={busy}
              error={error}
              needsRegistrationToken={registrationOpen === false}
              onSubmit={(pw, regToken) => guard(async () => {
                await create(pw, regToken);
                toast.success("Vault created and encrypted");
              })}
              onJoin={() => { setMode("join"); setError(""); }}
            />
          )}

          {mode === "unlock" && (
            <UnlockForm
              busy={busy}
              error={error}
              bioReady={bioReady}
              onSubmit={(pw) => guard(async () => {
                if (!(await unlock(pw))) throw new Error("That password doesn't match this vault.");
              })}
              onBiometric={() => guard(async () => {
                const pw = await Biometric.unlock();
                if (!pw || !(await unlock(pw))) throw new Error("Biometric unlock failed.");
              })}
              onJoin={() => { setMode("join"); setError(""); }}
              onReset={() => setConfirmReset(true)}
            />
          )}

          {mode === "join" && (
            <JoinForm
              busy={busy}
              error={error}
              onSubmit={(args) => guard(async () => {
                await join(args);
                toast.success("Vault joined — pulling your entries");
              })}
              onBack={() => { setMode(hasVault ? "unlock" : "setup"); setError(""); }}
            />
          )}
        </div>

        <div className="lock-foot">
          <LockIcon size={13} />
          <span>AES-256-GCM on this device. The server only ever stores ciphertext.</span>
        </div>
      </div>

      {confirmReset && (
        <ResetDialog onCancel={() => setConfirmReset(false)} />
      )}
    </div>
  );
}

function SetupForm({ busy, error, needsRegistrationToken, onSubmit, onJoin }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [regToken, setRegToken] = useState("");
  const strength = passwordStrength(pw);

  const invalid = pw.length < 8 || pw !== confirm || (needsRegistrationToken && !regToken);

  return (
    <form className="lock-form"
      onSubmit={(e) => { e.preventDefault(); if (!invalid) onSubmit(pw, regToken); }}
      autoComplete="off">
      <p className="lock-hint">
        Create a <strong>master password</strong>. It encrypts everything you store here,
        never leaves this device, and <strong>cannot be recovered</strong> — choose it carefully.
      </p>

      <div className="field">
        <label htmlFor="setup-pw">Master password</label>
        <input id="setup-pw" type="password" value={pw} autoFocus
          onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" />
        {pw && (
          <div className="row" style={{ gap: "var(--s-2)" }}>
            <div className="progress grow">
              <span style={{ width: `${strength.score * 25}%` }} />
            </div>
            <span className="tiny" style={{ color: `var(--${strength.tone})` }}>{strength.label}</span>
          </div>
        )}
      </div>

      <div className="field">
        <label htmlFor="setup-confirm">Confirm password</label>
        <input id="setup-confirm" type="password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
        {confirm && pw !== confirm && <div className="tiny" style={{ color: "var(--danger)" }}>Passwords don't match.</div>}
      </div>

      {needsRegistrationToken && (
        <div className="field">
          <label htmlFor="setup-reg">Server registration token</label>
          <input id="setup-reg" type="password" value={regToken}
            onChange={(e) => setRegToken(e.target.value)} placeholder="from the server operator" />
          <div className="hint-text">
            This server only accepts new vaults from someone holding its registration
            token. Existing vaults sync without it.
          </div>
        </div>
      )}

      <div className="error-text">{error}</div>

      <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy || invalid}>
        {busy ? <span className="spinner" /> : <LockIcon size={16} />}
        Create secure vault
      </button>
      <button type="button" className="btn-link" onClick={onJoin}>
        Join an existing synced vault
      </button>
    </form>
  );
}

function UnlockForm({ busy, error, bioReady, onSubmit, onBiometric, onJoin, onReset }) {
  const [pw, setPw] = useState("");
  return (
    <form className="lock-form" onSubmit={(e) => { e.preventDefault(); onSubmit(pw); }} autoComplete="off">
      <p className="lock-hint">Welcome back. Enter your master password to unlock.</p>

      <div className="field">
        <label htmlFor="unlock-pw">Master password</label>
        <input id="unlock-pw" type="password" value={pw} autoFocus
          onChange={(e) => setPw(e.target.value)} placeholder="Master password" />
      </div>

      <div className="error-text">{error}</div>

      <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={busy || !pw}>
        {busy ? <span className="spinner" /> : <LockIcon size={16} />}
        Unlock
      </button>

      {bioReady && (
        <button type="button" className="btn btn-ghost btn-block" onClick={onBiometric} disabled={busy}>
          <FingerprintIcon size={16} /> Unlock with biometrics
        </button>
      )}

      <div className="row between" style={{ marginTop: "var(--s-1)" }}>
        <button type="button" className="btn-link" onClick={onJoin}>Join a synced vault</button>
        <button type="button" className="btn-link danger" onClick={onReset}>Reset vault</button>
      </div>
    </form>
  );
}

function JoinForm({ busy, error, onSubmit, onBack }) {
  const [syncId, setSyncId] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");

  return (
    <form className="lock-form" autoComplete="off"
      onSubmit={(e) => { e.preventDefault(); onSubmit({ syncId, token, password }); }}>
      <p className="lock-hint">
        Pull a vault onto this device. You'll find the <strong>Sync ID</strong> and
        <strong> token</strong> under Settings → Sync on a device that already has it.
      </p>

      <div className="field">
        <label htmlFor="join-id">Sync ID</label>
        <input id="join-id" type="text" value={syncId} autoFocus className="mono"
          onChange={(e) => setSyncId(e.target.value.trim())} placeholder="e.g. 8f3c…" />
      </div>
      <div className="field">
        <label htmlFor="join-token">Sync token</label>
        <input id="join-token" type="password" value={token}
          onChange={(e) => setToken(e.target.value.trim())} placeholder="shared secret" />
      </div>
      <div className="field">
        <label htmlFor="join-pw">Master password</label>
        <input id="join-pw" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} placeholder="that vault's master password" />
      </div>

      <div className="error-text">{error}</div>

      <button type="submit" className="btn btn-primary btn-block btn-lg"
        disabled={busy || !syncId || !token || !password}>
        {busy ? <span className="spinner" /> : null} Join &amp; unlock
      </button>
      <button type="button" className="btn-link" onClick={onBack}>Back</button>
    </form>
  );
}

function ResetDialog({ onCancel }) {
  const { reset } = useVault();
  return (
    <ConfirmDialog
      title="Reset this vault?"
      danger
      confirmLabel="Erase everything on this device"
      message="Every entry, password, transaction and goal stored on this device will be erased, and without the master password none of it can be recovered. A copy on the sync server is not deleted."
      onCancel={onCancel}
      onConfirm={() => { reset(); onCancel(); }}
    />
  );
}
